import { PaginatedTable, SortByConfig } from "components/Table/Table";
import { IProject } from "interfaces/project";
import { CellProps, Column } from "react-table";
import { usePagination } from "hooks/table-hooks/usePagination";
import { styled } from "@mui/material/styles";

import { Paper } from "@mui/material";

import { Case, ICasesResponse } from "interfaces/case";
import { TooltipText } from "components/TooltipText";
import { CaseTableLinkCell } from "components/SharedTable/tableRenderers";
import { useContext, useState } from "react";
import { makeQueryString } from "core/makeQueryString";
import { useQuery } from "@tanstack/react-query";
import { API_URL_CASES } from "constants/api_urls";
import { AxiosContext } from "contexts/AxiosContext";

export interface IProjectCasesTableProps {
  selectedProject: IProject | undefined;
}

const columnDefinitions: Column<Case>[] = [
  {
    Header: "Case ID",
    id: "id",
    accessor: (data: Case) => data.display_id,
    Cell: (data: CellProps<Case>) =>
      CaseTableLinkCell({
        value: data.cell.value,
        caseId: data.cell.row.original.id
      })
  },
  {
    Header: "Site Name",
    id: "site_name",
    accessor: (data: Case) => data.site_name,
    Cell: ({ value }: CellProps<Case>) => TooltipText({ text: value })
  },
  {
    Header: "Machine Name",
    id: "machine_name",
    accessor: (data: Case) => data.machine_name,
    Cell: (cell: CellProps<Case>) => TooltipText({ text: cell.value })
  },
  {
    Header: "Status",
    id: "status",
    accessor: (data: Case) => data.case_status_name
  }
];

const CasesTableDefaultSort: SortByConfig[] = [
  {
    id: "id",
    desc: false
  }
];

export const ProjectCasesTable = ({
  selectedProject
}: IProjectCasesTableProps): JSX.Element => {
  const { axios } = useContext(AxiosContext)!;

  const {
    pageIndex: casesPageIndex,
    pageSize: casesPageSize,
    changePageIndex: casesChangePageIndex,
    changePageSize: casesChangePageSize
  } = usePagination();

  const [orderingGetCases, setOrderingGetCases] = useState<SortByConfig[]>(
    CasesTableDefaultSort
  );

  const fetchCasesQueryString = makeQueryString({
    page: casesPageIndex + 1,
    size: casesPageSize,
    project_id__in: selectedProject?.id,
    ordering: (orderingGetCases.length > 0
      ? orderingGetCases
      : CasesTableDefaultSort
    )
      .map(ord => `${ord.desc ? "-" : ""}${ord.id}`)
      .join(",")
  });

  const fetchCases = useQuery({
    enabled: selectedProject !== undefined,
    queryKey: ["cases", selectedProject, fetchCasesQueryString],
    queryFn: () =>
      axios
        .get<ICasesResponse>(`${API_URL_CASES}${fetchCasesQueryString}`)
        .then(r => r.data),
    cacheTime: 0,
    refetchOnWindowFocus: false
  });

  return (
    <TableContainer>
      <PaginatedTable
        data={fetchCases.data?.results || []}
        columns={columnDefinitions}
        loading={fetchCases.isLoading}
        dataQa="PP-Cases-Table"
        count={fetchCases.data?.count || 0}
        page={(fetchCases?.data?.count || 0) > 0 ? casesPageIndex : 0}
        pageSize={casesPageSize}
        onPageChange={(_event, p: number) => casesChangePageIndex(p)}
        onPageSizeChange={(
          event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
        ) => casesChangePageSize(parseInt(event.target.value))}
        onSortingChange={sorting =>
          setOrderingGetCases(
            sorting.map(s => ({
              id: s.id,
              desc: s.desc || false
            }))
          )
        }
        defaultSortBy={CasesTableDefaultSort}
      />
    </TableContainer>
  );
};

const TableContainer = styled(Paper)`
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid rgba(224, 224, 224, 1);
  border-radius: 4px;
  box-shadow: none;
`;
TableContainer.displayName = "TableContainer";
